import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Avatar,
  Skeleton,
  Link,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import NotesPanel from "bundles/Internal/components/common/NotesPanel";
import { ArrowLeft, ClipboardList, User, History } from "lucide-react";
import { STATUS_COLOR } from "bundles/Internal/components/job_application/JobApplicationTable";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import ApplyToOfferDialog from "bundles/Internal/components/common/ApplyToOfferDialog";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Archive, Edit } from "lucide-react";

const DATE_KEYS = new Set(["created_at", "updated_at"]);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const sectionPaperSx = {
  borderRadius: "0.75rem",
  overflow: "hidden",
  border: "1px solid hsl(140 8% 88%)",
  p: 4,
};

const sectionTitleSx = {
  color: "hsl(150 30% 12%)",
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  mb: 3,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const KeyValueTile = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "calc(0.75rem - 2px)",
      backgroundColor: "hsl(140 10% 95%)",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        color: "hsl(150 10% 46%)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontWeight: 600,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        color: "hsl(150 30% 12%)",
        mt: 0.5,
        fontSize: 14,
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}
    >
      {value}
    </Box>
  </Box>
);

const tableHeadCellSx = {
  backgroundColor: "transparent",
  borderBottom: "1px solid hsl(140 8% 88%)",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "hsl(150 10% 46%)",
  py: "10px",
  px: "16px",
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  whiteSpace: "nowrap",
  py: "14px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  maxWidth: 240,
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderBottom: "1px solid hsl(140 8% 88%)",
};

function DetailsTab({ jobApplication, loading, navigate }) {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(12)].map((_, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (!jobApplication) return null;

  const statusColor = STATUS_COLOR[jobApplication.status] || STATUS_COLOR.pending;

  const tiles = [
    { label: "Application ID", value: jobApplication.id },
    { label: "Status", value: jobApplication.status ? pill(jobApplication.status, statusColor) : "—" },
    { label: "Candidate", value: jobApplication.candidate?.id ? (
        <Box
          component="span"
          onClick={() => navigate(`/internal/candidates/${jobApplication.candidate.id}`)}
          sx={{ color: "#079344", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        >
          {jobApplication.candidate_name}
        </Box>
      ) : (jobApplication.candidate_name || "—") },
    { label: "Email", value: jobApplication.candidate_email ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="span">{jobApplication.candidate_email}</Box>
          <CopyButton value={jobApplication.candidate_email} />
        </Stack>
      ) : "—" },
    { label: "Phone", value: jobApplication.candidate_phone ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="span">{jobApplication.candidate_phone}</Box>
          <CopyButton value={jobApplication.candidate_phone} />
        </Stack>
      ) : "—" },
    { label: "Job Title", value: jobApplication.job_offer?.id ? (
        <Box
          component="span"
          onClick={() => navigate(`/internal/job_offers/${jobApplication.job_offer.id}`)}
          sx={{ color: "#079344", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        >
          {jobApplication.job_title}
        </Box>
      ) : (jobApplication.job_title || "—") },
    { label: "Company", value: jobApplication.company?.id ? (
        <Box
          component="span"
          onClick={() => navigate(`/internal/companies/${jobApplication.company.id}`)}
          sx={{ color: "#079344", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        >
          {jobApplication.company_name}
        </Box>
      ) : (jobApplication.company_name || "—") },
    {
      label: "Resume",
      value: jobApplication.resume_url ? (
        <Link
          href={jobApplication.resume_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "#079344",
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          View Resume
        </Link>
      ) : "—"
    },
    { label: "Applied At", value: formatDateTime(jobApplication.created_at) || "—" },
    { label: "Updated At", value: formatDateTime(jobApplication.updated_at) || "—" },
    { label: "Sent from Talent Team", value: jobApplication.sent_from_talent_team ? "Yes" : "No" },
    { label: "Added from CV Library", value: jobApplication.added_from_cvtheque ? "Yes" : "No" },
    { label: "UTM Source", value: jobApplication.utm_source || "—" },
    { label: "Denial Reasons", value: jobApplication.humanized_denial_reasons || "—" },
  ];

  return (
    <Stack spacing={4}>
      <Grid container spacing={3} alignItems="stretch">
        {tiles.map((t, idx) => (
          <Grid item xs={12} sm={6} key={`${t.label}-${idx}`}>
            <KeyValueTile label={t.label} value={t.value} />
          </Grid>
        ))}
      </Grid>

      {jobApplication.cover_letter && (
        <Box>
          <Typography
            sx={{
              color: "hsl(150 30% 12%)",
              fontWeight: 600,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              mb: 2,
            }}
          >
            Cover Letter
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: "calc(0.75rem - 2px)",
              backgroundColor: "hsl(140 10% 95%)",
              whiteSpace: "pre-wrap",
              fontSize: 14,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(150 30% 12%)",
            }}
          >
            {jobApplication.cover_letter}
          </Box>
        </Box>
      )}
    </Stack>
  );
}

function CandidateProfileTab({ candidate, loading }) {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(6)].map((_, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (!candidate) {
    return (
      <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
        No candidate information available.
      </Typography>
    );
  }

  const tiles = [
    { label: "Candidate ID", value: candidate.id },
    { label: "First Name", value: candidate.first_name || "—" },
    { label: "Last Name", value: candidate.last_name || "—" },
    { label: "Email", value: candidate.email ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="span">{candidate.email}</Box>
          <CopyButton value={candidate.email} />
        </Stack>
      ) : "—" },
    { label: "Phone", value: candidate.phone ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="span">{candidate.phone}</Box>
          <CopyButton value={candidate.phone} />
        </Stack>
      ) : "—" },
    { label: "Job Title", value: candidate.job_title || "—" },
  ];

  return (
    <Grid container spacing={3} alignItems="stretch">
      {tiles.map((t, idx) => (
        <Grid item xs={12} sm={6} key={`${t.label}-${idx}`}>
          <KeyValueTile label={t.label} value={t.value} />
        </Grid>
      ))}
    </Grid>
  );
}

function TimelineTab({ timeline, loading }) {
  if (loading) {
    return (
      <Stack spacing={1}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Stack>
    );
  }
  if (!timeline || timeline.length === 0) {
    return (
      <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
        No activity timeline available yet.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        overflowX: "auto",
        border: "1px solid hsl(140 8% 88%)",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Event", "From", "To", "Changed By", "Date"].map((label) => (
              <TableCell key={label} sx={tableHeadCellSx}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {timeline.map((event, idx) => {
            const fromColor = STATUS_COLOR[event.from_status] || STATUS_COLOR.pending;
            const toColor = STATUS_COLOR[event.to_status] || STATUS_COLOR.pending;

            return (
              <TableRow
                key={idx}
                hover
                sx={{
                  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                <TableCell sx={tableBodyCellSx}>{event.event_type || "Status Change"}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {event.from_status ? pill(event.from_status, fromColor) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {event.to_status ? pill(event.to_status, toColor) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{event.changed_by || "System"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDateTime(event.created_at) || "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

const TAB_KEYS = ["details", "candidate", "timeline"];

function JobApplicationShowPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [jobApplication, setJobApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const activeTab = Math.max(TAB_KEYS.indexOf(searchParams.get("tab")), 0);
  const setActiveTab = (v) => setSearchParams({ tab: TAB_KEYS[v] }, { replace: true, state: location.state });
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/job_applications"));
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchJobApplication = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    sh.get(`/internal/job_applications/${id}`)
      .then((res) => {
        setJobApplication(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to load job application");
      });
  }, [id]);

  useEffect(() => { fetchJobApplication(); }, [fetchJobApplication]);

  const handleDelete = useCallback(() => {
    setDeleting(true);
    sh.delete(`/internal/job_applications/${id}`, { data: { authenticity_token } })
      .then(() => {
        enqueueSnackbar("Job application archived", { variant: "success" });
        if (window.history.state?.idx > 0) navigate(-1);
        else navigate("/internal/job_applications");
      })
      .catch(() => enqueueSnackbar("Failed to archive job application", { variant: "error" }))
      .finally(() => { setDeleting(false); setDeleteDialogOpen(false); });
  }, [id, authenticity_token, enqueueSnackbar, navigate]);

  const title = useMemo(() => {
    if (!jobApplication) return "";
    return jobApplication.candidate_name || `Application #${jobApplication.id}`;
  }, [jobApplication]);

  const getInitials = () => {
    if (!jobApplication || !jobApplication.candidate_name) return "JA";
    const words = jobApplication.candidate_name.split(" ").filter(Boolean);
    if (words.length === 0) return "JA";
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Mock timeline data - in production, this would come from the backend
  const timeline = useMemo(() => {
    if (!jobApplication) return [];
    // This is placeholder data - you'll need to implement timeline tracking in the backend
    return [
      {
        event_type: "Application Created",
        from_status: null,
        to_status: "pending",
        changed_by: "System",
        created_at: jobApplication.created_at,
      },
    ];
  }, [jobApplication]);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 72px)",
        backgroundColor: "hsl(140 10% 95%)",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid hsl(140 8% 88%)",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ minWidth: 0 }}
            >
              <Button
                onClick={goBack}
                disableRipple
                sx={{
                  minWidth: 0,
                  p: 0.75,
                  color: "hsl(150 10% 46%)",
                  "&:hover": { color: "hsl(150 30% 12%)", backgroundColor: "transparent" },
                }}
              >
                <ArrowLeft size={20} />
              </Button>

              {loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
          <Skeleton width={140} height={20} />
                    <Skeleton width={200} height={14} />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 15,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      backgroundColor: "hsl(150 38% 17%)",
                      color: "hsl(80 97% 72%)",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 16, sm: 18 },
                          color: "hsl(150 30% 12%)",
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {title}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 0.25, flexWrap: "wrap", rowGap: 0.25 }}
                    >
                      {jobApplication?.job_title && (
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: "hsl(150 10% 46%)",
                          }}
                        >
                          {jobApplication.job_title}
                        </Typography>
                      )}
                      {jobApplication?.status && (
                        <Box>
                          {pill(
                            jobApplication.status,
                            STATUS_COLOR[jobApplication.status] || STATUS_COLOR.pending
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}
              {!loading && jobApplication && (
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Tooltip title="Edit application" arrow placement="bottom">
                    <IconButton
                      size="small"
                      onClick={() => setEditDialogOpen(true)}
                      sx={{ color: "hsl(150 10% 46%)", "&:hover": { color: "hsl(150 38% 17%)" } }}
                    >
                      <Edit size={18} strokeWidth={1.8} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Archive application" arrow placement="bottom">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialogOpen(true)}
                      sx={{ color: "hsl(150 10% 46%)", "&:hover": { color: "#D32F2F" } }}
                    >
                      <Archive size={18} strokeWidth={1.8} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Page Content */}
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Box sx={{ borderBottom: "1px solid hsl(140 8% 88%)", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: 13,
                    minHeight: 36,
                    py: 0.5,
                    px: 1.5,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "hsl(150 10% 46%)",
                    "&.Mui-selected": { color: "hsl(150 38% 17%)" },
                  },
                  "& .MuiTab-iconWrapper": { fontSize: 16 },
                  "& .MuiTabs-indicator": { backgroundColor: "hsl(150 38% 17%)" },
                }}
              >
                <Tab icon={<ClipboardList size={16} />} iconPosition="start" label="Details" />
                <Tab icon={<User size={16} />} iconPosition="start" label="Candidate Profile" />
                <Tab icon={<History size={16} />} iconPosition="start" label="Timeline" />
              </Tabs>
            </Box>

            <Paper elevation={0} sx={sectionPaperSx}>
              {error && (
                <Box
                  sx={{
                    p: 2,
                    m: 4,
                    backgroundColor: "hsla(0, 72%, 51%, 0.08)",
                    border: "1px solid hsla(0, 72%, 51%, 0.2)",
                    borderRadius: "calc(0.75rem - 2px)",
                  }}
                >
                  <Typography sx={{ color: "hsl(0 72% 51%)", fontSize: 14 }}>{error}</Typography>
                </Box>
              )}

              {TAB_KEYS[activeTab] === "details" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <ClipboardList size={20} color="hsl(150 38% 17%)" />
                    Application Details
                  </Typography>
                  <DetailsTab jobApplication={jobApplication} loading={loading} navigate={navigate} />
                </>
              )}

              {TAB_KEYS[activeTab] === "candidate" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <User size={20} color="hsl(150 38% 17%)" />
                    Candidate Profile
                  </Typography>
                  <CandidateProfileTab candidate={jobApplication?.candidate} loading={loading} />
                </>
              )}

              {TAB_KEYS[activeTab] === "timeline" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <History size={20} color="hsl(150 38% 17%)" />
                    Activity Timeline
                  </Typography>
                  <TimelineTab timeline={timeline} loading={loading} />
                </>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                position: { lg: "sticky" },
                top: { lg: 80 },
              }}
            >
              <NotesPanel resource="job_applications" recordId={id} />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {jobApplication && (
        <ApplyToOfferDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          jobApplication={jobApplication}
          onApplied={fetchJobApplication}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Archive Job Application"
        description="Are you sure you want to archive this job application? It will no longer appear in the platform."
        cancelLabel="Cancel"
        confirmLabel={deleting ? "Archiving…" : "Archive"}
        destructive
      />
    </Box>
  );
}

export default JobApplicationShowPage;
